// Compact task card used by the Kanban view.
//
// Differs from TaskRow (which is a wide tabular row): this is a vertical
// stack card with title, priority badge, assignee avatar, phase tag and a
// relative-time deadline ("in 3 Tagen" / "überfällig").

import { Diamond, Flag, GripVertical } from "lucide-preact";
import { AiOriginBadge } from "./AiOriginBadge";
import type { Phase, Task } from "@juuplan/shared";
import {
  TASK_PRIORITY_LABEL,
  TASK_PRIORITY_VAR,
} from "../lib/format";
import { AvatarStack } from "./AssigneePicker";

interface Props {
  task: Task;
  phase: Phase | null;
  projectColor: string | null;
  onOpen: () => void;
  /** dnd props — draggableProps go on the card, dragHandleProps on the grip. */
  draggableProps?: Record<string, unknown>;
  dragHandleProps?: Record<string, unknown>;
  innerRef?: (el: HTMLElement | null) => void;
  isDragging?: boolean;
  style?: preact.JSX.CSSProperties;
  /**
   * Show the drag grip. When false (drag disabled / read-only), the grip is
   * hidden and the card is not draggable. Defaults to true.
   */
  showDragHandle?: boolean;
}

export function TaskCard(props: Props): preact.JSX.Element {
  const {
    task,
    phase,
    projectColor,
    onOpen,
    draggableProps,
    dragHandleProps,
    innerRef,
    isDragging,
    style,
    showDragHandle = true,
  } = props;

  const accent = phase?.color ?? projectColor ?? "var(--color-primary)";
  const deadline = task.deadline ?? task.end_date;
  const relDeadline = relativeDay(deadline);

  return (
    <article
      ref={(el) => innerRef?.(el as HTMLElement | null)}
      {...(draggableProps ?? {})}
      style={style}
      class={`group relative flex items-stretch overflow-hidden rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-left text-sm hover:border-[color:var(--color-outline)] hover:shadow-level-2 focus-within:ring-2 focus-within:ring-[color:var(--color-primary)] focus-within:ring-offset-2 transition-[border-color,box-shadow] duration-150 ${
        isDragging ? "shadow-level-3 rotate-[0.5deg]" : ""
      }`}
    >
      {/* phase color stripe on the left */}
      <span
        class="w-1 shrink-0"
        style={{ background: accent }}
        aria-hidden="true"
      />

      {/* Drag grip — full-height, dedicated handle. A dedicated handle (rather
          than making the whole card the handle) is required because the title
          below is a <button>, and @hello-pangea/dnd blocks drags that start on
          interactive elements — which is why grabbing the card body "misses".
          touch-none lets it drag on touch devices without scrolling. */}
      {showDragHandle && (
        <span
          {...(dragHandleProps ?? {})}
          class="flex shrink-0 items-center justify-center w-6 cursor-grab touch-none self-stretch text-[color:var(--color-on-surface-variant)]/40 hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface-variant)] transition-colors"
          aria-label="Aufgabe verschieben"
          title="Zum Verschieben ziehen"
        >
          <GripVertical size={14} aria-hidden="true" />
        </span>
      )}

      <div class={`flex-1 min-w-0 py-2.5 pr-3 ${showDragHandle ? "pl-1" : "pl-3"}`}>
      <button
        type="button"
        onClick={onOpen}
        class="block w-full text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1"
        aria-label={`Aufgabe öffnen: ${task.title}`}
      >
        <div class="flex items-start gap-1.5 mb-1.5">
          {task.is_milestone && (
            <Diamond
              size={14}
              class="mt-0.5 shrink-0 text-[color:var(--color-prio-urgent)]"
              fill="currentColor"
              aria-label="Meilenstein"
            />
          )}
          <h4
            class={`flex-1 min-w-0 text-sm font-medium text-[color:var(--color-on-surface)] leading-snug ${
              task.status === "done" ? "line-through opacity-60" : ""
            }`}
          >
            {task.title}
          </h4>
          <AiOriginBadge origin={task.ai_origin} />
        </div>
      </button>

      <div class="flex items-center justify-between gap-2 flex-wrap mt-2">
        <div class="flex items-center gap-1.5 min-w-0">
          {task.ticket && (
            <span
              class="font-mono text-label font-medium text-[color:var(--color-on-surface-variant)] shrink-0"
              title="Ticketnummer"
            >
              {task.ticket}
            </span>
          )}
          {phase && (
            <span
              class="inline-flex items-center gap-1 rounded-sm border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-surface-variant)] truncate max-w-[120px]"
              title={phase.name}
            >
              <span
                class="inline-block size-1.5 rounded-full shrink-0"
                style={{ background: phase.color ?? accent }}
                aria-hidden="true"
              />
              <span class="truncate">{phase.name}</span>
            </span>
          )}
          {task.priority && (
            <span
              class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-label font-semibold shrink-0"
              style={{
                background: `color-mix(in srgb, ${TASK_PRIORITY_VAR[task.priority]} 14%, transparent)`,
                color: TASK_PRIORITY_VAR[task.priority],
              }}
              title={`Priorität: ${TASK_PRIORITY_LABEL[task.priority]}`}
            >
              <Flag size={14} class="shrink-0" fill="currentColor" aria-hidden="true" />
              {TASK_PRIORITY_LABEL[task.priority]}
            </span>
          )}
        </div>

        <div class="flex items-center gap-1.5 shrink-0">
          {relDeadline && (
            <span
              class={`text-label font-medium ${
                relDeadline.overdue
                  ? "text-[color:var(--color-error)]"
                  : "text-[color:var(--color-on-surface-variant)]"
              }`}
              title={`Deadline: ${deadline}`}
            >
              {relDeadline.label}
            </span>
          )}
          {task.assignee_ids.length > 0 ? (
            <AvatarStack userIds={task.assignee_ids} />
          ) : (
            <span
              class="inline-flex size-5 items-center justify-center rounded-full border border-dashed border-[color:var(--color-outline-variant)] text-label text-[color:var(--color-on-surface-variant)]"
              title="Nicht zugewiesen"
              aria-hidden="true"
            >
              —
            </span>
          )}
        </div>
      </div>
      </div>
    </article>
  );
}

// ---------- helpers ----------

/**
 * Returns a short German relative-day label ("heute", "in 3 Tagen", "überfällig (2T)").
 * Null if no date.
 */
export function relativeDay(
  iso: string | null
): { label: string; overdue: boolean } | null {
  if (!iso) return null;
  const today = startOfDay(new Date());
  const target = startOfDay(new Date(iso));
  if (Number.isNaN(target.getTime())) return null;
  const diffDays = Math.round(
    (target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
  );
  if (diffDays < 0) {
    const days = Math.abs(diffDays);
    return {
      label: days === 1 ? "überfällig (1T)" : `überfällig (${days}T)`,
      overdue: true,
    };
  }
  if (diffDays === 0) return { label: "heute", overdue: false };
  if (diffDays === 1) return { label: "morgen", overdue: false };
  if (diffDays < 7) return { label: `in ${diffDays} Tagen`, overdue: false };
  if (diffDays < 31) {
    const weeks = Math.round(diffDays / 7);
    return { label: weeks === 1 ? "in 1 Woche" : `in ${weeks} Wochen`, overdue: false };
  }
  // Far away — fall back to the date itself.
  return {
    label: target.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }),
    overdue: false,
  };
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
